import React, { useEffect, useMemo } from 'react';
import { Alert, Box, Typography, Tabs, Tab } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useFilters } from '@/contexts/FilterContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { useProductsMap } from '@/hooks/useProductsMap';
import { ProductRegistryTab } from '@/components/platform/ProductRegistryTab';
import { ProductScopeBar } from '@/components/platform/ProductScopeBar';
import { ProductTermsPanel } from '@/components/platform/ProductTermsPanel';
import { ProductTrialPanel } from '@/components/platform/ProductTrialPanel';
import { ProductBillingCredentialsTab } from '@/components/platform/ProductBillingCredentialsTab';
import { ProductTenantsPanel } from '@/components/platform/ProductTenantsPanel';
import { ProductPricingPanel } from '@/components/platform/ProductPricingPanel';
import { ProductVideosPanel } from '@/components/platform/ProductVideosPanel';
import { ProductEmailConfigTab } from '@/components/platform/ProductEmailConfigTab';
import { ProductSystemAdminsTab } from '@/components/platform/ProductSystemAdminsTab';

const TAB_KEYS = [
  'registry',
  'tenants',
  'pricing',
  'terms',
  'trial',
  'billing',
  'email',
  'systemadmins',
  'videos'
] as const;

type TabKey = (typeof TAB_KEYS)[number];

const PlatformProductsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { productId } = useFilters();
  const productsById = useProductsMap();
  const isFreeProduct = productId ? productsById.get(productId)?.isFree === true : false;
  const { setRefreshCallback } = useRefresh();
  const { can } = useAdminPermissions();

  const tabFromUrl = searchParams.get('tab');

  const visibleTabs = useMemo(() => {
    const tabs: { key: TabKey; label: string }[] = [
      { key: 'registry', label: 'Produtos' },
      { key: 'tenants', label: 'Clientes' },
      { key: 'pricing', label: 'Precificação' },
      { key: 'terms', label: 'Termos' },
      { key: 'trial', label: 'Período de teste' },
      { key: 'videos', label: 'Vídeos' }
    ];
    if (can('billing:read')) {
      tabs.push({ key: 'billing', label: 'Credenciais billing' });
    }
    tabs.push({ key: 'email', label: 'Email' });
    tabs.push({ key: 'systemadmins', label: 'System Admins' });
    return tabs;
  }, [can]);

  const activeTab: TabKey = useMemo(() => {
    if (!tabFromUrl || tabFromUrl === 'mrr') return 'registry';
    if (TAB_KEYS.includes(tabFromUrl as TabKey)) return tabFromUrl as TabKey;
    return 'registry';
  }, [tabFromUrl]);

  useEffect(() => {
    const legacyTabs = ['mrr', 'config'];
    if (tabFromUrl && legacyTabs.includes(tabFromUrl)) {
      setSearchParams({ tab: 'registry' }, { replace: true });
      return;
    }
    if (tabFromUrl && !visibleTabs.some(t => t.key === activeTab)) {
      setSearchParams({ tab: visibleTabs[0]?.key || 'registry' }, { replace: true });
    }
  }, [tabFromUrl, activeTab, visibleTabs, setSearchParams]);

  useEffect(() => {
    if (activeTab === 'registry') {
      setRefreshCallback(null);
    }
    return () => setRefreshCallback(null);
  }, [activeTab, setRefreshCallback]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const key = visibleTabs[newValue]?.key || 'registry';
    setSearchParams({ tab: key });
  };

  const tabValue = Math.max(
    0,
    visibleTabs.findIndex(t => t.key === activeTab)
  );

  const isProductTab = activeTab !== 'registry';

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Produtos
      </Typography>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {visibleTabs.map(t => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>

      {activeTab === 'registry' && <ProductRegistryTab />}

      {isProductTab && !productId && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Selecione um produto no filtro superior para visualizar o conteúdo desta aba.
        </Alert>
      )}

      {isProductTab && productId && (
        <Box key={productId}>
          <ProductScopeBar />
          {activeTab === 'tenants' && <ProductTenantsPanel />}
          {activeTab === 'pricing' && <ProductPricingPanel />}
          {activeTab === 'terms' && <ProductTermsPanel />}
          {(activeTab === 'trial' || activeTab === 'billing') && isFreeProduct && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Produto gratuito: período de teste e cobrança não se aplicam. Para alterar, edite o
              produto na aba Produtos.
            </Alert>
          )}
          {activeTab === 'trial' && !isFreeProduct && <ProductTrialPanel />}
          {activeTab === 'billing' && !isFreeProduct && can('billing:read') && (
            <ProductBillingCredentialsTab />
          )}
          {activeTab === 'email' && <ProductEmailConfigTab />}
          {activeTab === 'systemadmins' && <ProductSystemAdminsTab />}
          {activeTab === 'videos' && <ProductVideosPanel />}
        </Box>
      )}
    </Box>
  );
};

export default PlatformProductsPage;
